import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common/request';

/**
 * Response shape for `GET /twg-api/v1/microsite/metrics/global`.
 *
 * Source of truth: the BE controller in twg-api at
 * `src/app/controllers/microsite-metrics/types.ts` and the OpenAPI schema
 * `GlobalMicrositeHeroMetricsResponse` in twg-api/openapi.yml.
 *
 * Required fields (BE uses `requireNumber`/`requireString` which throw if the
 * dbt row is missing them, returning a 503 to the client) are non-nullable
 * here. The remaining fields can be `null` when the upstream dbt model has
 * not yet populated a value — treat `null` as "no data yet".
 *
 * Note: `organisationId` is typed as `string` (not the `'__global__'`
 * literal) to mirror the BE type definition; for the `/global` endpoint
 * the value is always `'__global__'` but we follow the upstream contract.
 */
export type GlobalMetricsResponse = {
	/** Always `'__global__'` for this endpoint, but typed as `string` to match the BE contract. */
	organisationId: string;
	totalRelationships: number;
	activeRelTypes: number;
	relDataAsOf: string;
	computedAt: string;
	cacheStatus: 'READY' | 'STALE';
	totalNodes: number | null;
	activeNodeTypes: number | null;
	nodeDataAsOf: string | null;
	avgDailyNodeGrowth: number | null;
	daysSinceSnapshot: number | null;
	/** Derived in the API layer from `totalNodes + avgDailyNodeGrowth × daysSinceSnapshot`. */
	estimatedNodesNow: number | null;
	avgFetchQueriesPerDay: number | null;
	queryDataAsOf: string | null;
};

/**
 * Fetch the platform-wide hero metrics used by the logged-out home view.
 *
 * Calls the Stargate-fronted route so the request is same-origin from the
 * microsite frontend. The endpoint is anonymous + edge-cached for 15 minutes;
 * no auth headers are required and no client-side cache layer is needed on top.
 */
export const requestGlobalMetrics = (): {
	controller: AbortController;
	execute: Promise<RequestResponse<GlobalMetricsResponse>>;
} => {
	const { controller, execute } = requestWithAbort<GlobalMetricsResponse>(
		'get',
		'/gateway/api/twg-api/v1/microsite/metrics/global',
		undefined,
		undefined,
		undefined,
		true,
	);
	return { controller, execute };
};
